import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { dominantDirection, formatTimecode, type DraftChapter } from "@rec/shared";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";

/**
 * Manual chapters, authored as split points.
 *
 * A chapter's end is always the next chapter's start (or the end of the
 * timeline), so the editor never offers a second handle to drag — overlaps and
 * gaps are unrepresentable rather than repaired. Deleting a chapter merges it
 * into the previous one, which is what removing a boundary means.
 *
 * The primary gesture is "play, and press the button where the topic changes".
 */
export function ChapterEditor({
  chapters,
  playheadMs,
  durationMs,
  onChange,
  onSeek,
}: {
  chapters: DraftChapter[];
  playheadMs: number;
  durationMs: number;
  onChange: (chapters: DraftChapter[]) => void;
  onSeek: (ms: number) => void;
}) {
  const { t } = useTranslation(["studio", "common"]);
  const sorted = [...chapters].sort((a, b) => a.startMs - b.startMs);

  /** Ends derived from the next start — the invariant, maintained on write. */
  const withEnds = (list: DraftChapter[]): DraftChapter[] => {
    const ordered = [...list].sort((a, b) => a.startMs - b.startMs);
    return ordered.map((chapter, i) => ({
      ...chapter,
      endMs: ordered[i + 1]?.startMs ?? Math.max(durationMs, chapter.startMs + 1),
    }));
  };

  const addAtPlayhead = () => {
    // Two chapters at one moment is one chapter; nudge instead of duplicating.
    if (sorted.some((c) => Math.abs(c.startMs - playheadMs) < 250)) return;

    onChange(
      withEnds([
        ...sorted,
        {
          title: t("chapters.defaultTitle", { number: sorted.length + 1 }),
          summary: "",
          startMs: Math.round(playheadMs),
          endMs: durationMs,
        },
      ]),
    );
  };

  const update = (index: number, patch: Partial<DraftChapter>) => {
    onChange(withEnds(sorted.map((c, i) => (i === index ? { ...c, ...patch } : c))));
  };

  const remove = (index: number) => {
    onChange(withEnds(sorted.filter((_, i) => i !== index)));
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border p-2">
        <p className="text-[12px] text-text-muted">
          {sorted.length === 0
            ? t("chapters.none")
            : t("chapters.count", { count: sorted.length })}
        </p>
        <Button variant="secondary" size="sm" onClick={addAtPlayhead}>
          <Plus size={13} />
          {t("chapters.addAt", { time: formatTimecode(playheadMs) })}
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
        {sorted.length === 0 ? (
          <p className="p-4 text-[13px] leading-relaxed text-text-muted">
            {t("chapters.emptyHelp")}
          </p>
        ) : (
          sorted.map((chapter, index) => (
            <div
              key={`${chapter.startMs}-${index}`}
              className="rounded-lg border border-border bg-surface p-2"
            >
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => onSeek(chapter.startMs)}
                  className="shrink-0 font-mono text-[11px] tabular-nums text-accent hover:underline"
                >
                  {formatTimecode(chapter.startMs)}
                </button>
                <button
                  type="button"
                  onClick={() => remove(index)}
                  title={
                    index === 0
                      ? t("chapters.removeFirst")
                      : t("chapters.removeMerge")
                  }
                  className="rounded p-1 text-text-faint hover:bg-surface-hover hover:text-danger"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <input
                value={chapter.title}
                dir={dominantDirection(chapter.title || "a")}
                onChange={(e) => update(index, { title: e.target.value })}
                placeholder={t("chapters.titlePlaceholder")}
                className={cn(
                  "mt-1 w-full rounded border border-transparent bg-transparent px-1 py-0.5",
                  "text-[13px] font-semibold text-text",
                  "focus:border-border focus:outline-none",
                )}
              />
              <textarea
                value={chapter.summary}
                dir={dominantDirection(chapter.summary || chapter.title || "a")}
                onChange={(e) => update(index, { summary: e.target.value })}
                placeholder={t("chapters.summaryPlaceholder")}
                rows={2}
                className={cn(
                  "mt-0.5 w-full resize-none rounded border border-transparent bg-transparent px-1 py-0.5",
                  "text-[12px] leading-relaxed text-text-muted",
                  "focus:border-border focus:outline-none",
                )}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
